import { theme as antdTheme, type ThemeConfig } from 'antd';
import type { MappingAlgorithm } from 'antd/es/theme/interface';
import { isPublicPlayerContext } from '@/utils/publicHost';

/**
 * Единая точка настройки темы приложения (WEB-386 PM-4).
 * Токены (цвет/радиус/шрифт) жили инлайном в App.tsx — собраны сюда, чтобы стиль
 * приложения настраивался в одном месте, а не был раскидан по компонентам.
 */
export type ResolvedTheme = 'light' | 'dark';

/** Базовые токены — общие для светлой и тёмной темы. */
export const appThemeToken = {
  colorPrimary: '#4094F7',
  borderRadius: 8,
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

/**
 * WEB-2237: все раскрывашки кабинета открываются одной кривой и одной
 * длительностью — шторочной (`cubic-bezier(0.645, 0.045, 0.355, 1)`, 0,3 с).
 * Кривая у antd уже такая (`motionEaseInOut` из семян), а вот анимация
 * сворачивания `Collapse` идёт по `motionDurationMid` = 0,2 с — короче общей.
 * Выравниваем длительность ТОЛЬКО у `Collapse`: общий токен движения менять
 * нельзя, от него зависят наведения и кнопки всего кабинета.
 */
export const COLLAPSE_MOTION_DURATION = '0.3s';

/**
 * Строит ThemeConfig для ConfigProvider под разрешённый режим.
 *
 * WEB-3587: при включённом переключателе пробы рескина (см. isLandingLookEnabled)
 * отдаёт пробную тему; без него — ровно прежний объект.
 */
export function buildAppTheme(mode: ResolvedTheme): ThemeConfig {
  if (isLandingLookEnabled()) return buildLandingProbeTheme(mode);
  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: appThemeToken,
    components: {
      Collapse: { motionDurationMid: COLLAPSE_MOTION_DURATION },
    },
  };
}

/* ======================= WEB-3587: проба темы рескина ======================= */

/**
 * Переключатель пробы: `localStorage['w2a-look'] === 'landing'` либо `?look=landing`
 * в адресе (адрес запоминает значение в localStorage; `?look=default` — снимает).
 * Других способов включения нет. В публичном плеере не включается никогда:
 * покупатель видит прежнюю тему, даже если открыл адрес с `?look=landing`.
 */
export const LANDING_LOOK_STORAGE_KEY = 'w2a-look';
const LANDING_LOOK_VALUE = 'landing';
const LANDING_LOOK_OFF_VALUE = 'default';

export function isLandingLookEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (isPublicPlayerContext()) return false;
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get('look');
  } catch {
    fromUrl = null;
  }
  if (fromUrl === LANDING_LOOK_VALUE || fromUrl === LANDING_LOOK_OFF_VALUE) {
    try {
      if (fromUrl === LANDING_LOOK_VALUE) {
        window.localStorage.setItem(LANDING_LOOK_STORAGE_KEY, LANDING_LOOK_VALUE);
      } else {
        window.localStorage.removeItem(LANDING_LOOK_STORAGE_KEY);
      }
    } catch {
      // Хранилище закрыто (приватный режим) — действует только адрес.
    }
    return fromUrl === LANDING_LOOK_VALUE;
  }
  try {
    return window.localStorage.getItem(LANDING_LOOK_STORAGE_KEY) === LANDING_LOOK_VALUE;
  } catch {
    return false;
  }
}

/**
 * Палитра единого макета рескина (спек-репа `design/cabinet-rescin/kit.jsx` @ 66ef93f7:
 * переменные `.ds[data-theme=…]` в начале `CSS` и их поправки вида «лендинг» в
 * `CSS_LANDING`; решения владельца 15.10 — WEB-3277). `color-mix(var(--fg) N%, transparent)`
 * переписан как цвет текста с прозрачностью N %.
 */
const LANDING_PALETTE = {
  dark: {
    page: '#08080a',
    card: '#121215',
    raised: '#1a1a1e',
    line: 'rgba(255, 255, 255, 0.075)',
    line2: 'rgba(255, 255, 255, 0.13)',
    fg: '#ededef',
    fg2: 'rgba(237, 237, 239, 0.62)',
    fg3: 'rgba(237, 237, 239, 0.52)',
    fg4: 'rgba(237, 237, 239, 0.26)',
    hover: 'rgba(237, 237, 239, 0.06)',
    press: 'rgba(237, 237, 239, 0.1)',
    link: '#4094f7',
    warn: '#e8b85c',
    err: '#f07171',
    popBg: 'rgba(12, 12, 14, 0.96)',
    shadow: '0 24px 48px -24px rgba(0, 0, 0, 0.8)',
    popShadow:
      'inset 0 0 0 1px rgba(176, 206, 242, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 24px 48px -20px rgba(0, 0, 0, 0.55)',
    fill5: 'rgba(237, 237, 239, 0.05)',
    fill7: 'rgba(237, 237, 239, 0.07)',
    fill10: 'rgba(237, 237, 239, 0.1)',
    fill11: 'rgba(237, 237, 239, 0.11)',
    btnHi: 'inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 1px 2px rgba(0, 0, 0, 0.28)',
  },
  light: {
    page: '#f5f6f8',
    card: '#ffffff',
    raised: '#ffffff',
    line: 'rgba(14, 16, 22, 0.13)',
    line2: 'rgba(14, 16, 22, 0.24)',
    fg: '#0e1016',
    fg2: 'rgba(14, 16, 22, 0.78)',
    fg3: 'rgba(14, 16, 22, 0.7)',
    fg4: 'rgba(14, 16, 22, 0.36)',
    hover: 'rgba(14, 16, 22, 0.06)',
    press: 'rgba(14, 16, 22, 0.09)',
    link: '#1f56d8',
    warn: '#a16207',
    err: '#d03b3b',
    popBg: 'rgba(255, 255, 255, 0.98)',
    shadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 16px 40px -16px rgba(16, 24, 40, 0.16)',
    popShadow: 'inset 0 0 0 1px rgba(14, 16, 22, 0.13), 0 24px 48px -20px rgba(16, 24, 40, 0.28)',
    fill5: 'rgba(14, 16, 22, 0.05)',
    fill7: 'rgba(14, 16, 22, 0.07)',
    fill10: 'rgba(14, 16, 22, 0.1)',
    fill11: 'rgba(14, 16, 22, 0.11)',
    btnHi: 'inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 1px 2px rgba(16, 24, 40, 0.18)',
  },
} as const;

/** Акцент одинаков в обеих темах (решение владельца 15.10: кнопки `#2a65f2`). */
const LANDING_ACCENT = {
  base: '#2a65f2',
  hover: '#2f71ff',
  press: '#275ddf',
  selected: 'rgba(42, 101, 242, 0.12)', // .opt[aria-selected] вида «лендинг»
} as const;

/**
 * Вторая функция алгоритма: ставит цвета макета поверх того, что насчитал
 * светлый/тёмный алгоритм. Одна на режим (постоянная ссылка), чтобы тема
 * при включённом переключателе была одинаковым объектом от вызова к вызову.
 */
function makePinColors(mode: ResolvedTheme): MappingAlgorithm {
  const p = LANDING_PALETTE[mode];
  return (seed, map) => ({
    ...(map ?? antdTheme.defaultAlgorithm(seed)),
    colorPrimary: LANDING_ACCENT.base,
    colorPrimaryHover: LANDING_ACCENT.hover,
    colorPrimaryActive: LANDING_ACCENT.press,
    colorLink: p.link,
    colorLinkHover: p.link,
    colorBgLayout: p.page,
    colorBgContainer: p.card,
    colorBgElevated: p.raised,
    colorText: p.fg,
    colorTextSecondary: p.fg2,
    colorTextTertiary: p.fg3,
    colorTextQuaternary: p.fg4,
    colorBorder: p.line2,
    colorBorderSecondary: p.line,
    colorError: p.err,
    colorWarning: p.warn,
  });
}

const LANDING_PIN_COLORS: Record<ResolvedTheme, MappingAlgorithm> = {
  dark: makePinColors('dark'),
  light: makePinColors('light'),
};

/**
 * Пробная тема рескина (WEB-3587) — только токены Ant Design, общие и по
 * компонентам; никаких перекрытий классов `.ant-*`. Что токеном не берётся —
 * результат пробы, строка отчёта, а не правка стилей.
 *
 * Свои цвета кладутся ВТОРОЙ функцией алгоритма поверх светлого/тёмного:
 * тёмный алгоритм перекрашивает заданный цвет (`#2a65f2` → `#2759d1`, WEB-3277).
 */
export function buildLandingProbeTheme(mode: ResolvedTheme): ThemeConfig {
  const p = LANDING_PALETTE[mode];
  const pinColors = LANDING_PIN_COLORS[mode];
  const field = {
    borderRadius: 10,
    colorBgContainer: p.fill5,
    colorBorder: 'transparent',
    hoverBorderColor: 'transparent',
    activeBorderColor: 'transparent',
  };
  return {
    algorithm: [mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm, pinColors],
    token: {
      colorPrimary: LANDING_ACCENT.base,
      fontFamily: `'Nunito Sans', ${appThemeToken.fontFamily}`,
      // плотность «плотно» (.ds[data-density=compact]; отказ владельца В-3 от 16.10
      // перекрывает размеры 15.10 — решение оркестратора 19.10)
      fontSize: 13,
      lineHeight: 1.55,
      borderRadius: 10,
      controlHeight: 32,
      // производные, которые алгоритм иначе считает от fontSize/borderRadius сам
      fontSizeSM: 12,
      fontSizeHeading3: 20,
      lineHeightHeading3: 1.2,
      controlHeightSM: 28,
      borderRadiusSM: 8,
      borderRadiusXS: 4,
      borderRadiusLG: 12,
      colorSplit: p.line,
      colorTextPlaceholder: p.fg4,
      controlItemBgHover: p.hover,
    },
    components: {
      Collapse: { motionDurationMid: COLLAPSE_MOTION_DURATION },
      Button: {
        borderRadius: 999,
        borderRadiusSM: 999,
        borderRadiusLG: 999,
        // класс .btn макета задаёт 600, но `.ds button { font: inherit }` перебивает —
        // макет рисует 400, его владелец и видел (решение оркестратора 19.10)
        fontWeight: 400,
        // текст в 12 от края у обеих кнопок («плотно»): рамка 1 px у Ant Design внутри
        paddingInline: 11,
        paddingInlineSM: 11,
        contentFontSize: 13,
        contentFontSizeSM: 13,
        primaryColor: '#fff',
        primaryShadow: p.btnHi,
        defaultShadow: 'none',
        defaultBg: p.fill7,
        defaultHoverBg: p.fill11,
        defaultActiveBg: p.fill11,
        defaultBorderColor: p.line2,
        defaultHoverBorderColor: p.line2,
        defaultActiveBorderColor: p.line2,
        defaultColor: p.fg,
        defaultHoverColor: p.fg,
        defaultActiveColor: p.fg,
      },
      Input: {
        ...field,
        paddingInline: 11, // + рамка 1 px = текст в 12 от края, как в макете
        activeShadow: 'none',
        hoverBg: p.fill5,
        activeBg: p.fill5,
      },
      Select: {
        ...field,
        activeOutlineColor: 'transparent',
        optionHeight: 28,
        optionFontSize: 13,
        optionActiveBg: p.hover,
        optionSelectedBg: LANDING_ACCENT.selected,
        optionSelectedFontWeight: 400,
        colorBgElevated: p.popBg,
        borderRadiusLG: 18,
        boxShadowSecondary: p.popShadow,
      },
      Switch: {
        trackHeight: 20,
        trackMinWidth: 36,
        handleSize: 16,
        trackPadding: 2,
        colorTextQuaternary: p.press,
        colorTextTertiary: p.press,
      },
      Checkbox: {
        controlInteractiveSize: 18,
        borderRadiusSM: 4,
        colorBorder: p.fg3,
        colorBgContainer: 'transparent',
        lineWidth: 1.5,
      },
      Segmented: {
        trackPadding: 2,
        trackBg: p.fill5,
        itemSelectedBg: p.fill10,
        itemColor: p.fg3,
        itemSelectedColor: p.fg,
        itemHoverBg: 'rgba(0, 0, 0, 0)',
        itemHoverColor: p.fg,
        itemActiveBg: p.fill10,
        borderRadius: 999,
        borderRadiusSM: 999,
        borderRadiusXS: 999,
        // плашка = controlHeight − 2 × trackPadding = 24 у обоих размеров, как в макете
        controlHeight: 28,
        controlHeightSM: 28,
        fontSize: 12,
        // Ant Design вычитает из отступа подписи рамку 1 px → 12 и 8, как в макете
        controlPaddingHorizontal: 13,
        controlPaddingHorizontalSM: 9,
        boxShadowTertiary: 'none',
      },
      Table: {
        // все три размера таблицы одной шкалой: витрина кита рисует таблицу малой
        // (size="small"), и без SM/MD-токенов размер ячейки шёл из умолчаний —
        // это и была находка WEB-3277 «cellFontSize не применился»
        cellFontSize: 13,
        cellFontSizeMD: 13,
        cellFontSizeSM: 13,
        cellPaddingInline: 12,
        cellPaddingInlineMD: 12,
        cellPaddingInlineSM: 12,
        // строка макета — ровно 40, шапка 36 при нулевом отступе (содержимое по центру);
        // у Ant Design высота = отступы + содержимое, 8 даёт шапку и строку текста ≈ 37
        cellPaddingBlock: 8,
        cellPaddingBlockMD: 8,
        cellPaddingBlockSM: 8,
        headerBg: 'transparent',
        headerColor: p.fg3,
        headerSplitColor: 'transparent',
        borderColor: p.line,
        rowHoverBg: p.hover,
        headerBorderRadius: 12,
        colorBgContainer: p.card,
      },
      Modal: {
        contentBg: p.popBg,
        headerBg: p.popBg,
        footerBg: 'transparent',
        borderRadiusLG: 18,
        titleFontSize: 17,
        titleColor: p.fg,
        fontWeightStrong: 700,
        boxShadow: p.popShadow,
      },
      Tooltip: {
        colorBgSpotlight: p.raised,
        colorTextLightSolid: p.fg,
        borderRadius: 8,
        fontSize: 12,
        paddingSM: 10,
        paddingXS: 10,
        // наименьшая высота подсказки у Ant Design — controlHeight; в макете высоту
        // задаёт содержимое (12 × 1,55 + 5 + 5 ≈ 28,6)
        controlHeight: 28,
        boxShadowSecondary: `inset 0 0 0 1px ${p.line2}, ${p.shadow}`,
      },
      Typography: { fontWeightStrong: 700 },
    },
  };
}
